import React from "react";
import PolicyLayout, { PolicyH2, PolicyP, PolicyUL, PolicyCallout } from "../components/app/PolicyLayout";

const TOC = [
  { id: "tldr", label: "The short version" },
  { id: "who", label: "1. Who we are" },
  { id: "what", label: "2. What we collect" },
  { id: "why", label: "3. Why we collect it" },
  { id: "share", label: "4. Who we share it with" },
  { id: "retention", label: "5. How long we keep it" },
  { id: "rights", label: "6. Your GDPR rights" },
  { id: "security", label: "7. How we protect it" },
  { id: "kids", label: "8. Children" },
  { id: "international", label: "9. International transfers" },
  { id: "complaints", label: "10. Complaints" },
];

export default function Privacy() {
  return (
    <PolicyLayout
      title="Privacy Policy"
      eyebrow="Legal · UK GDPR · Plain English"
      lastUpdated="23 April 2026"
      toc={TOC}
      testid="privacy-page"
    >
      <PolicyCallout>
        <strong id="tldr">The short version:</strong> we collect your email and what you do in the app so it works.
        We never sell your data, we never run ads, and you can export or delete everything with one email.
      </PolicyCallout>

      <PolicyH2 id="who">1. Who we are (Data Controller)</PolicyH2>
      <PolicyP>
        For UK GDPR purposes, the data controller is <strong>Anthony Slater</strong>, a sole trader
        operating CampCompass UK from <strong>10 Cyprus Street, Darwen, Lancashire, BB3 2JX, United Kingdom</strong>.
        We are registered with the UK Information Commissioner's Office (ICO) under reference
        number <strong data-testid="privacy-ico-number">CSN5693592</strong>. You can verify our entry on
        the public register at{" "}
        <a href="https://ico.org.uk/esdwebpages/search" className="link-underline text-[#ff4500]" target="_blank" rel="noopener noreferrer">ico.org.uk/esdwebpages/search</a>.
        For any data protection enquiry, reach our Data Protection contact at{" "}
        <a href="mailto:dpo@camp-compass.uk" className="link-underline text-[#ff4500]">dpo@camp-compass.uk</a>.
      </PolicyP>

      <PolicyH2 id="what">2. What data we collect</PolicyH2>
      <PolicyUL>
        <li><strong>Account:</strong> email, name, password hash (never your plain password), profile picture if you sign in with Google.</li>
        <li><strong>App activity:</strong> campsites you favourite, trip dates you save, reviews you post, community threads and replies.</li>
        <li><strong>Device &amp; location:</strong> IP address, browser type, the location you type in when you search for campsites. We never access your GPS.</li>
        <li><strong>Chat logs:</strong> your conversations with Ask Camper (AI). Stored so the assistant has context across turns. Deleted 90 days after your last message in a session.</li>
        <li><strong>Analytics (with consent):</strong> anonymous usage events via PostHog — only if you accepted analytics cookies.</li>
        <li><strong>Payments:</strong> if you subscribe to Pro, Stripe handles your card. We only see a customer ID and subscription status, never card details.</li>
      </PolicyUL>

      <PolicyH2 id="why">3. Why we collect it (legal bases)</PolicyH2>
      <PolicyUL>
        <li><strong>Contract:</strong> to run your account, show you favourites, process Pro subscriptions.</li>
        <li><strong>Legitimate interests:</strong> to moderate the community, prevent abuse, keep the app secure.</li>
        <li><strong>Consent:</strong> for analytics cookies and marketing emails (both can be withdrawn any time).</li>
        <li><strong>Legal obligation:</strong> tax records for Pro payments (retained 6 years per HMRC).</li>
      </PolicyUL>

      <PolicyH2 id="share">4. Who we share it with (data processors)</PolicyH2>
      <PolicyP>
        We use a small number of trusted processors who are contractually bound by UK GDPR terms:
      </PolicyP>
      <PolicyUL>
        <li><strong>Stripe</strong> — subscription payments (data stays in EU/UK).</li>
        <li><strong>Emergent LLM (Anthropic Claude)</strong> — Ask Camper AI conversations.</li>
        <li><strong>PostHog</strong> — anonymous analytics, only with your consent.</li>
        <li><strong>Open-Meteo</strong> — weather forecasts. We send lat/lng, no user info.</li>
        <li><strong>OpenStreetMap / Nominatim</strong> — campsite coordinates and location search.</li>
        <li><strong>Pitchup.com</strong> — when you click "Book on Pitchup", you leave our app (see their privacy policy).</li>
      </PolicyUL>
      <PolicyP>
        <strong>We never sell your data.</strong> We never share it with advertisers. We never run third-party ads.
      </PolicyP>

      <PolicyH2 id="retention">5. How long we keep your data</PolicyH2>
      <PolicyUL>
        <li>Account data — until you close your account, then deleted within 30 days.</li>
        <li>Chat logs — 90 days from your last message.</li>
        <li>Weather-alert notifications — auto-pruned 24 hours after trip start date.</li>
        <li>New-campsite notifications — auto-pruned after 30 days.</li>
        <li>Payment records — 6 years (HMRC requirement).</li>
        <li>Community posts (reviews/threads) — kept while the account exists; anonymised if you ask us to close the account but keep the thread alive for other members.</li>
      </PolicyUL>

      <PolicyH2 id="rights">6. Your rights under UK GDPR</PolicyH2>
      <PolicyP>You can, at any time:</PolicyP>
      <PolicyUL>
        <li><strong>Access</strong> — ask for a copy of everything we hold on you.</li>
        <li><strong>Rectify</strong> — correct anything that's wrong.</li>
        <li><strong>Erase</strong> — close your account and have your data deleted.</li>
        <li><strong>Export</strong> — get a machine-readable copy to take elsewhere.</li>
        <li><strong>Restrict / object</strong> — stop certain uses (e.g. analytics).</li>
        <li><strong>Withdraw consent</strong> — for cookies, emails, or anything else you opted into.</li>
      </PolicyUL>
      <PolicyCallout>
        Email <a href="mailto:dpo@camp-compass.uk" className="link-underline text-[#ff4500]">dpo@camp-compass.uk</a> and we'll
        respond within 30 days — usually in a day or two.
      </PolicyCallout>

      <PolicyH2 id="security">7. How we protect your data</PolicyH2>
      <PolicyUL>
        <li>HTTPS everywhere. JWT auth with short-lived tokens.</li>
        <li>Passwords hashed with bcrypt (industry-standard, never stored in plain text).</li>
        <li>MongoDB encrypted at rest.</li>
        <li>Access to production data is restricted to named operators.</li>
        <li>We'll notify the ICO and affected users within 72 hours of any qualifying breach.</li>
      </PolicyUL>

      <PolicyH2 id="kids">8. Children</PolicyH2>
      <PolicyP>
        CampCompass is not intended for anyone under 13. We don't knowingly collect data from children. If you
        believe a child has made an account, please email us and we'll delete it.
      </PolicyP>

      <PolicyH2 id="international">9. International transfers</PolicyH2>
      <PolicyP>
        Most of our processing happens inside the UK/EU. Where data must leave the UK (e.g. Anthropic's Claude API
        is hosted in the US), we rely on Standard Contractual Clauses (SCCs) and the UK International Data Transfer
        Addendum — both ICO-approved safeguards.
      </PolicyP>

      <PolicyH2 id="complaints">10. Complaints</PolicyH2>
      <PolicyP>
        We'd much rather hear from you first — email us and we'll sort it. But you can also complain to the UK
        Information Commissioner's Office at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="link-underline text-[#ff4500]">ico.org.uk</a> or 0303 123 1113.
      </PolicyP>
    </PolicyLayout>
  );
}
